import { Button, Input, Label, Textarea, useToast } from "@nfp-tracker/components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GraduationCap, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  loadInstructorProfile,
  upsertInstructorProfile,
} from "../integrations/supabase/instructor";
import { handleExternalError } from "../utils/errorSnackbar";

/**
 * The identity clients see when they share a chart with an instructor. Lives inside the shared
 * Settings page (instructors have no separate settings surface) and only renders for
 * `role = 'instructor'` accounts.
 */
export const InstructorProfileSection = ({ userId }: { userId: string }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [displayName, setDisplayName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [publicEmail, setPublicEmail] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);

  const profileQuery = useQuery({
    queryKey: ["instructor-profile", userId],
    queryFn: () => loadInstructorProfile(userId),
  });

  useEffect(() => {
    const profile = profileQuery.data;
    if (!profile) return;
    setDisplayName(profile.displayName);
    setCompanyName(profile.companyName || "");
    setPublicEmail(profile.publicEmail || "");
    setBio(profile.bio || "");
  }, [profileQuery.data]);

  const handleSave = async () => {
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      toast({
        title: "Name required",
        description: "Clients need a name to recognize you by.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await upsertInstructorProfile({
        userId,
        displayName: trimmedName,
        companyName,
        publicEmail,
        bio,
        // No directory exists yet; preserve whatever the row already holds.
        isListedPublicly: profileQuery.data?.isListedPublicly ?? false,
      });
      await queryClient.invalidateQueries({ queryKey: ["instructor-profile", userId] });
      toast({ title: "Instructor profile saved" });
    } catch (error) {
      handleExternalError(toast, error, { logPrefix: "Error saving instructor profile:" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t">
      <div>
        <Label className="text-base font-semibold flex items-center gap-2">
          <GraduationCap className="h-4 w-4" />
          Instructor Profile
        </Label>
        <p className="text-sm text-muted-foreground mt-1">
          Clients see this when they share their chart with you.
        </p>
      </div>

      {profileQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading profile…</p>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="instructor-display-name">Display name</Label>
            <Input
              id="instructor-display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Jane Doe, FCP"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="instructor-company">Practice or organization (optional)</Label>
            <Input
              id="instructor-company"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="instructor-email">Contact email shown to clients</Label>
            <Input
              id="instructor-email"
              type="email"
              inputMode="email"
              value={publicEmail}
              onChange={(event) => setPublicEmail(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="instructor-bio">Short bio (optional)</Label>
            <Textarea
              id="instructor-bio"
              value={bio}
              rows={3}
              onChange={(event) => setBio(event.target.value)}
            />
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Instructor Profile
          </Button>

          <p className="text-xs text-muted-foreground">
            Instructor accounts are not billed. Each client grants and can revoke your access from
            their own settings.
          </p>
        </div>
      )}
    </div>
  );
};
